import axios, { AxiosError, AxiosResponse } from "axios";
import { isValidToken, setSession } from "./jwt";
import { msalInstance } from "../App";
import { Payment1099 } from "../types/payment1099";
import { pdfavailable } from "../types/pdfavailable";
import { Authenticate } from "../types/Authenticate";
import { tokenRequest } from "../authConfig";
import {
  CSITipaltiAnalysisParams,
  UpdateCSITipaltiBody,
} from "../types/CSITypes";

const sleep = () => new Promise((resolve) => setTimeout(resolve, 250));

axios.defaults.baseURL = process.env.REACT_APP_API_URL;
const responseBody = (response: AxiosResponse) => response.data;

axios.interceptors.request.use(async (config) => {
  try {
    // Check if MSAL is initialized
    if (!msalInstance) {
      throw new Error("MSAL instance not available");
    }

    const account = msalInstance.getActiveAccount();
    if (!account) {
      // No active account, redirect to login
      setSession(null);
      msalInstance.loginRedirect(tokenRequest);
      return config;
    }

    // Try to get token silently first using the correct scope
    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account: account,
      });

      if (tokenResponse.accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
        // Store token for other uses
        localStorage.setItem("accessToken", tokenResponse.accessToken);
      }
    } catch (error) {
      // Silent token acquisition failed, try interactive
      console.warn("Silent token acquisition failed, redirecting to login");
      msalInstance.loginRedirect(tokenRequest);
    }
  } catch (error) {
    console.error("Error in axios request interceptor:", error);
    // Fallback to localStorage token if MSAL fails
    const token = localStorage.getItem("accessToken");
    if (token && isValidToken(token) && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  return config;
});

axios.interceptors.response.use(
  async (response) => {
    if (process.env.NODE_ENV === "development") await sleep();
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      const { data, status } = error.response as AxiosResponse;
      console.error(`API Error: ${status} - ${JSON.stringify(data)}`);
      switch (status) {
        case 400:
          if (data.errors) {
            const modelStateErrors: string[] = [];
            for (const key in data.errors) {
              if (data.errors[key]) {
                modelStateErrors.push(data.errors[key]);
              }
            }
            throw modelStateErrors.flat();
          }
          console.error(data.message);
          break;
        case 401:
          console.error("Unauthorized: " + data.message);
          break;
        case 403:
          console.error("Forbidden: You are not allowed to do that!");
          break;
        case 404:
          console.error(`Not Found: ${error.config?.url}`);
          break;
        case 500:
          console.error("Server error: " + data.message);
          break;
        default:
          console.error("Network error occurred: " + status);
          break;
      }
      return Promise.reject(error.response);
    }

    console.error("Network error: Unable to reach the server");
    return Promise.reject(
      new Error("Network error: Unable to reach the server")
    );
  }
);

const requests = {
  get: (url: string) => axios.get(url).then(responseBody),
  getFile: (url: string) =>
    axios.get(url, { responseType: "arraybuffer" }).then(responseBody),
  post: (url: string, body: {}) => axios.post(url, body).then(responseBody),
  put: (url: string, body: {}) => axios.put(url, body).then(responseBody),
  delete: (url: string) => axios.delete(url).then(responseBody),
  postFile: (url: string, body: {}) =>
    axios.post(url, body, { responseType: "blob" }).then(responseBody),
  postForm: (url: string, data: FormData) =>
    axios
      .post(url, data, {
        headers: { "Content-type": "multipart/form-data" },
      })
      .then(responseBody),
  putForm: (url: string, data: FormData) =>
    axios
      .put(url, data, {
        headers: { "Content-type": "multipart/form-data" },
      })
      .then(responseBody),
};

const Payees = {
  getAllPayees: (
    page: number,
    rowsPerPage: number,
    sortColumn: string,
    sortOrder: string,
    searchTerm?: string
  ) =>
    requests.get(
      `/Payees?pageIndex=${page}&pageSize=${rowsPerPage}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&searchTerm=${searchTerm}`
    ),
  getPayee: (id: string) => requests.get(`/payees/${id}`),
  getPayeeDetail: (id: string) => requests.get(`/payees/GetPayeeDetails/${id}`),
  DecryptTin: (encryptedTin: string) =>
    requests.get(
      `/Payees/decrypt?encryptedTin=${encodeURIComponent(encryptedTin)}`
    ),
};

const Bills = {
  getAllBills: (
    page: number,
    rowsPerPage: number,
    sortColumn: string,
    sortOrder: string,
    searchTerm?: string
  ) =>
    requests.get(
      `/Bills?pageIndex=${page}&pageSize=${rowsPerPage}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&searchTerm=${searchTerm}`
    ),
  getAllFailedBills: (
    page: number,
    rowsPerPage: number,
    sortColumn: string,
    sortOrder: string,
    searchTerm?: string
  ) =>
    requests.get(
      `/Bills/GetFailedBills?pageIndex=${page}&pageSize=${rowsPerPage}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&searchTerm=${searchTerm}`
    ),
  getBillByRefCode: (billRefCode: string) =>
    requests.get(`/Bills/GetByRefcode/${billRefCode}`),
  convertToCheck: (billRefCodes: string[], realmId?: string) =>
    requests.post(
      `/Bills/ConvertToCheck${
        realmId ? `?RealmId=${encodeURIComponent(realmId)}` : ""
      }`,
      billRefCodes
    ),
  reprocessFailedBills: (invoiceId: string) =>
    requests.post(`/Bills/ReprocessFailedBills?InvoiceId=${invoiceId}`, {}),
};

const CSITipalti = {
  getAllAnalysis: (
    pageIndex: CSITipaltiAnalysisParams["pageIndex"],
    pageSize: CSITipaltiAnalysisParams["pageSize"],
    isActive: CSITipaltiAnalysisParams["isActive"] = true,
    searchTerm: CSITipaltiAnalysisParams["searchTerm"] = null,
    sortColumn: CSITipaltiAnalysisParams["sortColumn"] = "CsiCreatedDate",
    sortOrder: CSITipaltiAnalysisParams["sortOrder"] = "desc",
    inTipalti: boolean | null = null,
    isMatched: boolean | null = null,
    isPayable: boolean | null = null,
    groupsMode: string = "all",
    hasMigratedBills: boolean | null = null,
    hasNonMigratedBills: boolean | null = null,
    csiPaymentMethod: string = "all",
    reviewedResults: string = "all"
  ) => {
    const params = new URLSearchParams({
      pageIndex: String(Math.max(1, pageIndex)),
      pageSize: String(pageSize),
      sortColumn,
      sortOrder,
    });
    if (isActive !== null) params.append("isActive", String(isActive));
    if (searchTerm) params.append("searchTerm", searchTerm);
    if (inTipalti !== null) params.append("inTipalti", String(inTipalti));
    if (isMatched !== null) params.append("isMatched", String(isMatched));
    if (isPayable !== null) params.append("isPayable", String(isPayable));
    if (groupsMode !== "all") params.append("groupsMode", groupsMode);
    if (hasMigratedBills !== null)
      params.append("hasMigratedBills", String(hasMigratedBills));
    if (hasNonMigratedBills !== null)
      params.append("hasNonMigratedBills", String(hasNonMigratedBills));
    if (csiPaymentMethod !== "all")
      params.append("csiPaymentMethod", csiPaymentMethod);
    if (reviewedResults !== "all")
      params.append("reviewedResults", reviewedResults);

    const url = `/CSITipalti/CSITipaltiStagings?${params.toString()}`;
    console.log("API URL being called:", url);

    return requests.get(url);
  },

  updateCSITipalti: (id: string, body: UpdateCSITipaltiBody) =>
    requests.post(`/CSITipalti/CSITipaltiStagings/${id}/update`, body),
};

const Payments = {
  getAllPaymentsByYear: (
    Year: string,
    page?: number,
    rowsPerPage?: number,
    sortColumn?: string,
    sortOrder?: string,
    searchTerm?: string
  ) => {
    const params = new URLSearchParams();
    params.append("year", Year);
    if (page !== undefined) {
      params.append("pageIndex", page.toString());
    }
    if (rowsPerPage !== undefined) {
      params.append("pageSize", rowsPerPage.toString());
    }
    if (sortColumn) {
      params.append("sortColumn", sortColumn);
    }
    if (sortOrder) {
      params.append("sortOrder", sortOrder);
    }
    if (searchTerm) {
      params.append("searchTerm", searchTerm);
    }
    return requests.get(`/Tax/1099?${params.toString()}`);
  },
  getAllPaymentDetailsByYear: (
    Year: string,
    page?: number,
    rowsPerPage?: number,
    sortColumn?: string,
    sortOrder?: string,
    searchTerm?: string,
    paymentCountFilter?: string,
    clientCountFilter?: string
  ) => {
    const params = new URLSearchParams();
    params.append("year", Year);
    if (page !== undefined) {
      params.append("pageIndex", page.toString());
    }
    if (rowsPerPage !== undefined) {
      params.append("pageSize", rowsPerPage.toString());
    }
    if (sortColumn) {
      params.append("sortColumn", sortColumn);
    }
    if (sortOrder) {
      params.append("sortOrder", sortOrder);
    }
    if (searchTerm) {
      params.append("searchTerm", searchTerm);
    }
    if (paymentCountFilter) {
      params.append("paymentCountFilter", paymentCountFilter);
    }
    if (clientCountFilter) {
      params.append("clientCountFilter", clientCountFilter);
    }
    return requests.get(`/Tax/1099/details?${params.toString()}`);
  },
  getTaxYear: () => requests.get(`/Tax/taxYears`),
  download1099PDF: (data: Payment1099) =>
    requests.postFile(`/Tax/CreatePDF1099`, data),
};

const Options = {
  getTaxPdfOptionByYear: (year: string) =>
    requests.get(`/Options/GetTaxPdfOptionByYear/${year}`),
  updateTaxPdfOption: (data: pdfavailable) =>
    requests.post(`/Options/UpdateTaxYearDisplay`, data),
};

const Tax2025 = {
  getAllPaymentsByYear: (
    Year: string,
    page?: number,
    rowsPerPage?: number,
    sortColumn?: string,
    sortOrder?: string,
    searchTerm?: string
  ) => {
    const params = new URLSearchParams();
    params.append("year", Year);
    if (page !== undefined) {
      params.append("pageIndex", page.toString());
    }
    if (rowsPerPage !== undefined) {
      params.append("pageSize", rowsPerPage.toString());
    }
    if (sortColumn) {
      params.append("sortColumn", sortColumn);
    }
    if (sortOrder) {
      params.append("sortOrder", sortOrder);
    }
    if (searchTerm) {
      params.append("searchTerm", searchTerm);
    }
    return requests.get(`/Tax2025/1099?${params.toString()}`);
  },
  getAllPaymentDetailsByYear: (
    Year: string,
    page?: number,
    rowsPerPage?: number,
    sortColumn?: string,
    sortOrder?: string,
    searchTerm?: string,
    paymentCountFilter?: string,
    clientCountFilter?: string
  ) => {
    const params = new URLSearchParams();
    params.append("year", Year);
    if (page !== undefined) {
      params.append("pageIndex", page.toString());
    }
    if (rowsPerPage !== undefined) {
      params.append("pageSize", rowsPerPage.toString());
    }
    if (sortColumn) {
      params.append("sortColumn", sortColumn);
    }
    if (sortOrder) {
      params.append("sortOrder", sortOrder);
    }
    if (searchTerm) {
      params.append("searchTerm", searchTerm);
    }
    if (paymentCountFilter) {
      params.append("paymentCountFilter", paymentCountFilter);
    }
    if (clientCountFilter) {
      params.append("clientCountFilter", clientCountFilter);
    }
    return requests.get(`/Tax2025/1099/details?${params.toString()}`);
  },
  getTaxYear: () => requests.get(`/Tax2025/taxYears`),
  download1099PDF: (data: Payment1099) =>
    requests.postFile(`/Tax2025/CreatePDF1099`, data),
};

const Invoices = {
  getInvoiceCounts: () => requests.get(`/Invoice/GetInvoiceCounts`),
  getDistinctInvoices: () => requests.get(`/Invoice/GetDistinctInvoices`),
  getInvoicesById: (invoiceId: string) =>
    requests.get(`/Invoice/GetInvoicesByInvoiceId/${invoiceId}`),
};

const quickbooks = {
  getAuthUrl: () => requests.get("/QuickBooks/GetQuickBooksAuthUrl"),
  getCompanies: () => requests.get("/QuickBooks/GetCompanies"),
  authenticateQuickbooks: (
    code: Authenticate,
    redirect_uri: string,
    realmId?: string
  ) =>
    requests.post(
      `QuickBooks/Authenticate?redirectUri=${redirect_uri}${
        realmId ? `&realmId=${encodeURIComponent(realmId)}` : ""
      }`,
      code
    ),
};

// AB#6563: Maintenance page — in-process jobs (first: Payment Ref Code backfill).
const Maintenance = {
  access: () => requests.get(`/Maintenance/access`),
  status: (jobName: string) =>
    requests.get(`/Maintenance/jobs/${encodeURIComponent(jobName)}/status`),
  start: (jobName: string, dryRun: boolean) =>
    requests.post(
      `/Maintenance/jobs/${encodeURIComponent(jobName)}/start?dryRun=${dryRun}`,
      {}
    ),
  cancel: (jobName: string) =>
    requests.post(
      `/Maintenance/jobs/${encodeURIComponent(jobName)}/cancel`,
      {}
    ),
};

const agent = {
  Payees,
  Bills,
  CSITipalti,
  Payments,
  Options,
  Invoices,
  quickbooks,
  Tax2025,
  Maintenance,
};

export default agent;
